import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import DealDetailsFormCard from './components/DealDetailsFormCard/DealDetailsFormCard.component';
import ColleaguesInDealCard from './components/ColleaguesInDealCard';
import TileContainer from '../../components/TileContainer/TileContainer.component';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  DocumentsStats,
  TileCardProps,
} from '../../components/TileContainer/utils';
import ListingContext from '../../providers/Listing/Listing.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import AddCustomNdaCard from '../../subrouters/ListingInformation/components/AddCustomNdaCard';
import HelmetWrapper from 'components/HelmetWrapper';
import AddDocsToEmptyDataRoom from './components/AddDocsToEmptyDataRoom';
import {
  checkIsPublishedOrPrivateDrafted,
  doesValueExist,
  getDataRoomRoute,
  getSellingPortfolio,
} from 'utils';
import LinkToIcon from 'icons/LinkTo.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import DealContext from '../../providers/Deal/Deal.context';
import { GetBuyersBanner } from 'components/GetBuyersBanner';
import { AddContactsToDeal } from './components/AddContactsToDeal';
import { MarketYourDeal } from './components/MarketYourDeal';
import DataRoomIsLockedCard from './components/DataRoomIsLockedCard';
import { NEW_WINDOW_PROPS } from 'data/global';
import DataRoomIsLockedUserCard from './components/DataRoomIsLockedUserCard';
import LoadingCard from 'components/LoadingCard';
import { LEAD_STAGES } from 'models/Lead';

import './Deal.styles.scss';
import './Deal.styles.responsive.scss';

type DealProps = {
  className?: string;
};

const Deal: React.FC<DealProps> = (props) => {
  const { className } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const classes = classNames('deal', className);

  const { isSuperAdmin, isRWCMember } = useContext(CurrentUserContext);
  const {
    dealRoom,
    canManageDeal,
    canAccessDataRoom,
    areActionsDisabled,
    hasUnlockPremiumFeatures,
    dashboardStats,
    statsIsFetching,
    listing: { _id, type },
    enquiry: {
      numberOfEnquiredLeads,
      numberOfAcceptedLeads,
      numberOfConfirmedLeads,
      numberOfOfferedLeads,
    },
  } = useContext(ListingContext);
  const { showGetBuyers, leadStagesCopies, isDataRoomAdmin } =
    useContext(DealContext);

  const { files, questions } = dashboardStats || {};

  const dealRoomId = dealRoom._id;

  const isLoadingTiles = statsIsFetching;

  const isPublishedOrPrivateDrafted = checkIsPublishedOrPrivateDrafted(
    dealRoom?.listingStage,
    dealRoom?.wantToBePrivate,
  );

  const hasLeads = useMemo(
    () =>
      !!(
        numberOfEnquiredLeads +
        numberOfAcceptedLeads +
        numberOfConfirmedLeads +
        numberOfOfferedLeads
      ),
    [
      numberOfAcceptedLeads,
      numberOfConfirmedLeads,
      numberOfEnquiredLeads,
      numberOfOfferedLeads,
    ],
  );

  const showLeadsTile = useMemo(
    () => isRWCMember || hasLeads || isPublishedOrPrivateDrafted,
    [hasLeads, isPublishedOrPrivateDrafted, isRWCMember],
  );

  const hasDataRoomFiles = useMemo(() => !!files, [files]);

  const dataRoomRoute = useMemo(
    () => getDataRoomRoute(dealRoom.dataRoomId, 'documents'),
    [dealRoom],
  );

  const leadsRoute = useMemo(
    () => `${getSellingPortfolio(dealRoomId, 'leads')}?sort=-lastSeen`,
    [dealRoomId],
  );

  const documentsInfo: Record<DocumentsStats, TileCardProps> = useMemo(
    () => ({
      files: { label: 'files', to: dataRoomRoute, openInNewTab: true },
      buySideMembers: {
        label: 'buySideMembers',
        ...(isDataRoomAdmin && {
          openInNewTab: true,
          to: getDataRoomRoute(dealRoom.dataRoomId, 'members'),
        }),
      },
      secureLinks: {
        label: 'secureLinks',
        ...(isDataRoomAdmin && {
          openInNewTab: true,
          to: getDataRoomRoute(dealRoom.dataRoomId, 'shareable-links'),
        }),
      },
      ...(doesValueExist(questions) && {
        questions: {
          label: 'questionsAsked',
          openInNewTab: true,
          to: getDataRoomRoute(
            dealRoom.dataRoomId,
            'questions/questions-and-answers',
          ),
        },
      }),
    }),
    [questions, dataRoomRoute, dealRoom.dataRoomId, isDataRoomAdmin],
  );

  const DataRoomTile = useMemo(() => {
    return isLoadingTiles ? (
      <LoadingCard className="deal-card" isLoading={isLoadingTiles} />
    ) : !hasUnlockPremiumFeatures ? (
      isSuperAdmin ? (
        <DataRoomIsLockedCard className="deal-card" />
      ) : (
        <DataRoomIsLockedUserCard className="deal-card" />
      )
    ) : !hasDataRoomFiles ? (
      <AddDocsToEmptyDataRoom className="deal-card green" />
    ) : (
      <TileContainer
        title={t('dataRoom')}
        className="deal-card"
        infoData={documentsInfo}
        countData={dashboardStats}
        {...(canAccessDataRoom && {
          tileContainerActions: (
            <Link
              className="as-button ncoded-button--outline"
              to={dataRoomRoute}
              {...NEW_WINDOW_PROPS}
            >
              {t('dataRoom')}
              <LinkToIcon />
            </Link>
          ),
        })}
      />
    );
  }, [
    isLoadingTiles,
    dashboardStats,
    dataRoomRoute,
    hasDataRoomFiles,
    hasUnlockPremiumFeatures,
    isSuperAdmin,
    canAccessDataRoom,
    documentsInfo,
    t,
  ]);

  const LEADS_INFO = useMemo(
    () => ({
      numberOfEnquiredLeads: { label: leadStagesCopies[LEAD_STAGES.ENQUIRE] },
      numberOfAcceptedLeads: { label: leadStagesCopies[LEAD_STAGES.ACCEPT] },
      numberOfConfirmedLeads: { label: leadStagesCopies[LEAD_STAGES.CONFIRM] },
      numberOfOfferedLeads: { label: leadStagesCopies[LEAD_STAGES.OFFER] },
    }),
    [leadStagesCopies],
  );

  const COUNT_DATA = useMemo(
    () => ({
      numberOfEnquiredLeads,
      numberOfAcceptedLeads,
      numberOfConfirmedLeads,
      numberOfOfferedLeads,
    }),
    [
      numberOfAcceptedLeads,
      numberOfConfirmedLeads,
      numberOfEnquiredLeads,
      numberOfOfferedLeads,
    ],
  );

  const ListingInfoTile = useMemo(() => {
    return isLoadingTiles ? (
      <LoadingCard className="deal-card" isLoading={isLoadingTiles} />
    ) : showLeadsTile ? (
      <TileContainer
        title={t('leads')}
        infoData={LEADS_INFO}
        className="deal-card"
        countData={COUNT_DATA}
        {...(canManageDeal && {
          handleOnClick: () => navigate(leadsRoute),
          tileContainerActions: (
            <Link className="as-button ncoded-button--outline" to={leadsRoute}>
              {t('viewLeads')}
              <ArrowrightIcon />
            </Link>
          ),
        })}
      />
    ) : showGetBuyers ? (
      <GetBuyersBanner
        className="deal-card green"
        hasImage
        disabled={areActionsDisabled}
        direction="column"
        dealRoomId={dealRoomId}
        listingId={_id}
        listingType={type}
      />
    ) : (
      <MarketYourDeal className="deal-card" />
    );
  }, [
    _id,
    canManageDeal,
    isLoadingTiles,
    leadsRoute,
    areActionsDisabled,
    dealRoomId,
    LEADS_INFO,
    COUNT_DATA,
    showGetBuyers,
    showLeadsTile,
    type,
    t,
    navigate,
  ]);

  return (
    <div className={classes}>
      <HelmetWrapper title={`Deal overview - ${dealRoom?.name}`} />

      <div className="deal__container">
        <div className="deal__container__left">
          {hasDataRoomFiles ? (
            <>
              {ListingInfoTile}
              {DataRoomTile}
            </>
          ) : (
            <>
              {DataRoomTile}
              {ListingInfoTile}
            </>
          )}
        </div>

        <div className="deal__container__right">
          <AddContactsToDeal className="deal-card" />
        </div>
      </div>

      <div className="deal__container">
        <div className="deal__container__left colleagues-container">
          <ColleaguesInDealCard isDisabled={areActionsDisabled} />
        </div>

        <div className="deal__container__right">
          <AddCustomNdaCard className="deal-card" />

          <DealDetailsFormCard
            className="deal-card"
            isDisabled={areActionsDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default Deal;
